import { Icon, Modal, SimpleGrid, Text, useToast } from '@melio/penny';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useSubscription } from '@melio/subscriptions-sdk';

import { usePlanInfo } from '../../utils';
import { currentFeaturesList } from './mocks';

type SubscriptionCancelModalActivityProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

export const SubscriptionCancelModalActivity = ({
  isOpen,
  onClose,
  onSuccess,
}: SubscriptionCancelModalActivityProps) => {
  const [isPlatformSubscriptionsEnabled] = useFeature(FeatureFlags.PlatformSubscriptions, false);
  const { subscription } = useSubscription();
  const { formatMessage, formatDate } = useMelioIntl();
  const toast = useToast();
  const isModalOpen = isOpen && isPlatformSubscriptionsEnabled;
  const { planName } = usePlanInfo({ planId: subscription?.planId });

  const formattedNextBillingDate = formatDate(subscription?.nextBillingDate, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const onSubmit = () => {
    const onCancelPromise = new Promise((resolve) => resolve(true));
    void onCancelPromise
      .then(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      })
      .catch(() => {
        toast({ type: 'error', title: formatMessage('activities.subscription.cancelModal.cancel.toast.error') });
      });
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={onClose}
      header={formatMessage('activities.subscription.cancelModal.title', { planName })}
      description={formatMessage('activities.subscription.cancelModal.description', { date: formattedNextBillingDate })}
      primaryButton={{
        label: formatMessage('activities.subscription.cancelModal.primaryButton'),
        onClick: onSubmit,
        variant: 'critical',
      }}
      secondaryButton={{
        label: formatMessage('activities.subscription.cancelModal.secondaryButton'),
        onClick: onClose,
        variant: 'tertiary',
      }}
      data-testid="subscription-cancel-modal-activity"
    >
      {currentFeaturesList.map((feature) => (
        <SimpleGrid key={feature} alignItems="flex-start" display="flex" gap="xs" paddingBottom="xxs">
          <Text color="critical.main">
            <Icon
              type="cancel-circle"
              color="inherit"
              size="small"
              aria-label={formatMessage('activities.subscription.cancelModal.list.ariaLabel')}
            />
          </Text>
          <Text textStyle="body3">{feature}</Text>
        </SimpleGrid>
      ))}
    </Modal>
  );
};
